import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already logged in on app start
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        if (apiService.isAuthenticated()) {
          const response = await apiService.getCurrentUser();
          if (response.success) {
            setUser(response.data);
          } else {
            // Token is invalid, clear it
            apiService.logout();
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        apiService.logout();
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await apiService.login(email, password);
      
      if (response.success) {
        setUser(response.data);
        return { success: true, message: 'Login successful' };
      } else {
        setError(response.message || 'Login failed');
        return { success: false, message: response.message || 'Login failed' };
      }
    } catch (error) {
      setError(error.message);
      return { success: false, message: error.message };
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      const response = await apiService.register(userData);
      
      if (response.success) {
        setUser(response.data);
        return { success: true, message: 'Registration successful' };
      } else {
        setError(response.message || 'Registration failed');
        return { success: false, message: response.message || 'Registration failed' };
      }
    } catch (error) {
      setError(error.message);
      return { success: false, message: error.message };
    }
  };

  const logout = () => {
    apiService.logout();
    setUser(null);
    setError(null);
  };

  const updateProfile = async (profileData) => {
    try {
      setError(null);
      const response = await apiService.updateProfile(profileData);
      
      if (response.success) {
        // Update user data in context
        setUser(prevUser => ({ ...prevUser, ...profileData }));
        return { success: true, message: 'Profile updated successfully' };
      } else {
        setError(response.message || 'Profile update failed');
        return { success: false, message: response.message || 'Profile update failed' };
      }
    } catch (error) {
      setError(error.message);
      return { success: false, message: error.message };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      setError(null);
      const response = await apiService.changePassword(currentPassword, newPassword);
      
      if (response.success) {
        return { success: true, message: 'Password changed successfully' };
      } else {
        setError(response.message || 'Password change failed');
        return { success: false, message: response.message || 'Password change failed' };
      }
    } catch (error) {
      setError(error.message);
      return { success: false, message: error.message };
    }
  };

  const isAuthenticated = () => {
    return !!user;
  };

  const hasRole = (role) => {
    return user && user.role === role;
  };

  const isPatient = () => {
    return hasRole('patient');
  };

  const isDoctor = () => {
    return hasRole('doctor');
  };

  const isAdmin = () => {
    return hasRole('admin');
  };

  const loginSuperAdmin = async (email, password) => {
  try {
    setError(null);
    const response = await apiService.loginSuperAdmin(email, password);

    if (response.success) {
      setUser(response.data); // ✅ update context
      return { success: true, message: 'Super admin login successful' };
    } else {
      setError(response.message || 'Login failed');
      return { success: false, message: response.message || 'Login failed' };
    }
  } catch (error) {
    setError(error.message);
    return { success: false, message: error.message };
  }
};


  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    isAuthenticated,
    hasRole,
    isPatient,
    isDoctor,
    isAdmin,
    loginSuperAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 